import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer, List,
  ListItemButton, ListItemText, Container, Menu, MenuItem,
  Divider, Tooltip, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import MergeIcon from '@mui/icons-material/Merge';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CompressIcon from '@mui/icons-material/Compress';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import ImageIcon from '@mui/icons-material/Image';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import HealingIcon from '@mui/icons-material/Healing';
import BadgeIcon from '@mui/icons-material/Badge';
import CodeIcon from '@mui/icons-material/Code';
import PanoramaIcon from '@mui/icons-material/Panorama';
import CropIcon from '@mui/icons-material/Crop';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import DrawIcon from '@mui/icons-material/Draw';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useThemeMode } from '../context/ThemeContext';

const Logo = ({ onClick }) => (
  <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', textDecoration: 'none' }}>
    <Box component="img" src="/logo.png" alt="TG Edit-Pdf" sx={{ width: 38, height: 38, borderRadius: '10px', objectFit: 'contain' }} />
    <Box sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.2rem', color: 'text.primary' }}>
      TG <Box component="span" sx={{ color: 'primary.main' }}>Edit-Pdf</Box>
    </Box>
  </Box>
);

export const CATEGORIES = [
  {
    labelKey: 'nav.cat_organize',
    tools: [
      { labelKey: 'nav.merge',       path: '/',             icon: <MergeIcon fontSize="small" /> },
      { labelKey: 'nav.mergePdfImages', path: '/merge-pdf-images', icon: <PermMediaIcon fontSize="small" /> },
      { labelKey: 'nav.split',       path: '/split-pdf',    icon: <ContentCutIcon fontSize="small" /> },
      { labelKey: 'nav.organize',    path: '/organize-pdf', icon: <DashboardCustomizeIcon fontSize="small" /> },
      { labelKey: 'nav.deletePages', path: '/delete-pages', icon: <DeleteSweepIcon fontSize="small" /> },
      { labelKey: 'nav.extract',     path: '/extract-pdf',  icon: <ContentCopyIcon fontSize="small" /> },
      { labelKey: 'nav.rotate',      path: '/rotate-pdf',   icon: <RotateRightIcon fontSize="small" /> },
      { labelKey: 'nav.numberPages', path: '/number-pages', icon: <FormatListNumberedIcon fontSize="small" /> },
      { labelKey: 'nav.createPdf',   path: '/create-pdf',   icon: <NoteAddIcon fontSize="small" /> },
    ],
  },
  {
    labelKey: 'nav.cat_optimize',
    tools: [
      { labelKey: 'nav.compress', path: '/compress-pdf', icon: <CompressIcon fontSize="small" /> },
      { labelKey: 'nav.repair',   path: '/repair-pdf',   icon: <HealingIcon fontSize="small" /> },
      { labelKey: 'nav.crop',     path: '/crop-pdf',     icon: <CropIcon fontSize="small" /> },
      { labelKey: 'nav.resize',   path: '/resize-pdf',   icon: <AspectRatioIcon fontSize="small" /> },
      { labelKey: 'nav.flatten',  path: '/flatten-pdf',  icon: <LayersClearIcon fontSize="small" /> },
    ],
  },
  {
    labelKey: 'nav.cat_convert',
    tools: [
      { labelKey: 'nav.wordToPdf',   path: '/word-to-pdf',   icon: <DescriptionIcon fontSize="small" /> },
      { labelKey: 'nav.excelToPdf',  path: '/excel-to-pdf',  icon: <TableChartIcon fontSize="small" /> },
      { labelKey: 'nav.pptToPdf',    path: '/powerpoint-to-pdf', icon: <CoPresentIcon fontSize="small" /> },
      { labelKey: 'nav.htmlToPdf',   path: '/html-to-pdf',   icon: <CodeIcon fontSize="small" /> },
      { labelKey: 'nav.pngToPdf',    path: '/png-to-pdf',    icon: <PanoramaIcon fontSize="small" /> },
      { labelKey: 'nav.pdfToJpg',    path: '/pdf-to-jpg',    icon: <PhotoCameraBackIcon fontSize="small" /> },
      { labelKey: 'nav.toImages',    path: '/pdf-to-images', icon: <ImageIcon fontSize="small" /> },
      { labelKey: 'nav.imagesToPdf', path: '/images-to-pdf', icon: <PhotoLibraryIcon fontSize="small" /> },
      { labelKey: 'nav.officeToPdf', path: '/office-to-pdf', icon: <PictureAsPdfIcon fontSize="small" /> },
      { labelKey: 'nav.pdfToWord',   path: '/pdf-to-word',   icon: <ArticleIcon fontSize="small" /> },
      { labelKey: 'nav.pdfToPpt',    path: '/pdf-to-ppt',    icon: <SlideshowIcon fontSize="small" /> },
      { labelKey: 'nav.pdfToPdfA',   path: '/pdf-to-pdfa',   icon: <Inventory2Icon fontSize="small" /> },
      { labelKey: 'nav.ocr',         path: '/ocr-pdf',       icon: <DocumentScannerIcon fontSize="small" /> },
    ],
  },
  {
    labelKey: 'nav.cat_edit',
    tools: [
      { labelKey: 'nav.edit',      path: '/edit-pdf',      icon: <EditIcon fontSize="small" /> },
      { labelKey: 'nav.metadata',  path: '/edit-metadata', icon: <BadgeIcon fontSize="small" /> },
      { labelKey: 'nav.sign',      path: '/sign-pdf',      icon: <DrawIcon fontSize="small" /> },
      { labelKey: 'nav.watermark', path: '/watermark-pdf', icon: <BrandingWatermarkIcon fontSize="small" /> },
    ],
  },
  {
    labelKey: 'nav.cat_security',
    tools: [
      { labelKey: 'nav.protect', path: '/protect-pdf', icon: <LockIcon fontSize="small" /> },
      { labelKey: 'nav.unlock',  path: '/unlock-pdf',  icon: <LockOpenIcon fontSize="small" /> },
    ],
  },
];

const RESOURCES = [
  { labelKey: 'nav.blog',     path: '/blog',     icon: <ArticleIcon fontSize="small" /> },
  { labelKey: 'nav.glossary', path: '/glossary', icon: <MenuBookIcon fontSize="small" /> },
  { labelKey: 'nav.help',     path: '/help',     icon: <HelpOutlineIcon fontSize="small" /> },
  { labelKey: 'nav.about',    path: '/about',    icon: <InfoOutlinedIcon fontSize="small" /> },
  { labelKey: 'nav.contact',  path: '/contact',  icon: <MailOutlineIcon fontSize="small" /> },
];

export default function Header() {
  const { t } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [toolsAnchor, setToolsAnchor] = useState(null);
  const [resourcesAnchor, setResourcesAnchor] = useState(null);
  const dark = mode === 'dark';

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollToTool = () => {
    if (location.pathname === '/') {
      document.getElementById('tool')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setDrawerOpen(false);
  };

  const goHome = () => {
    navigate('/');
    setTimeout(() => document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // Nav items are real <a href> links (RouterLink) for crawlability; this just closes the menus.
  const handleToolClick = () => {
    setToolsAnchor(null);
    setResourcesAnchor(null);
    setDrawerOpen(false);
  };

  const appBarBg = dark
    ? scrolled ? 'rgba(15,15,26,0.92)' : 'transparent'
    : scrolled ? 'rgba(255,255,255,0.92)' : 'transparent';

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: appBarBg,
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(99,102,241,0.1)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', px: '0 !important', py: 1 }}>
            <motion.div whileHover={{ scale: 1.03 }}>
              <Logo onClick={goHome} />
            </motion.div>

            {/* Desktop nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <Button
                onClick={goHome}
                sx={{ color: 'text.primary', fontWeight: 500, '&:hover': { color: 'primary.main', background: 'rgba(99,102,241,0.06)' } }}
              >
                {t('nav.home')}
              </Button>

              {/* Tools mega-menu */}
              <Button
                onClick={(e) => setToolsAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: toolsAnchor ? 'rotate(180deg)' : 'none' }} />}
                sx={{ color: 'text.primary', fontWeight: 500, '&:hover': { color: 'primary.main', background: 'rgba(99,102,241,0.06)' } }}
              >
                {t('nav.tools')}
              </Button>
              <Menu
                anchorEl={toolsAnchor}
                open={Boolean(toolsAnchor)}
                onClose={() => setToolsAnchor(null)}
                PaperProps={{ sx: { mt: 1, borderRadius: 3, boxShadow: '0 12px 48px -10px rgba(99,102,241,0.28)' } }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, maxWidth: 720 }}>
                  {CATEGORIES.map((cat) => (
                    <Box key={cat.labelKey} sx={{ minWidth: 168 }}>
                      <Typography variant="overline" sx={{ px: 1.5, color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
                        {t(cat.labelKey)}
                      </Typography>
                      {cat.tools.map((tool) => (
                        <MenuItem key={tool.path} component={RouterLink} to={tool.path}
                          onClick={handleToolClick}
                          sx={{ gap: 1.5, py: 1, borderRadius: 2, color: 'text.primary', '&:hover': { color: 'primary.main', background: 'rgba(99,102,241,0.06)' } }}>
                          <Box sx={{ color: 'primary.main', display: 'flex' }}>{tool.icon}</Box>
                          {t(tool.labelKey)}
                        </MenuItem>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Menu>

              {/* Resources menu */}
              <Button
                onClick={(e) => setResourcesAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: resourcesAnchor ? 'rotate(180deg)' : 'none' }} />}
                sx={{ color: 'text.primary', fontWeight: 500, '&:hover': { color: 'primary.main', background: 'rgba(99,102,241,0.06)' } }}
              >
                {t('nav.resources')}
              </Button>
              <Menu
                anchorEl={resourcesAnchor}
                open={Boolean(resourcesAnchor)}
                onClose={() => setResourcesAnchor(null)}
                PaperProps={{ sx: { mt: 1, borderRadius: 3, boxShadow: '0 12px 48px -10px rgba(99,102,241,0.28)' } }}
              >
                {RESOURCES.map((item) => (
                  <MenuItem key={item.path} component={RouterLink} to={item.path}
                    onClick={handleToolClick}
                    sx={{ gap: 1.5, py: 1, mx: 1, borderRadius: 2, color: 'text.primary', '&:hover': { color: 'primary.main', background: 'rgba(99,102,241,0.06)' } }}>
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>{item.icon}</Box>
                    {t(item.labelKey)}
                  </MenuItem>
                ))}
              </Menu>

              <LanguageSelector />

              <Tooltip title={dark ? t('nav.lightMode') : t('nav.darkMode')}>
                <IconButton onClick={toggleMode} sx={{ color: 'text.primary', ml: 0.5 }}>
                  {dark ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>

              <Button variant="contained" color="primary" onClick={scrollToTool} sx={{ ml: 1 }}>
                {t('hero.cta')}
              </Button>
            </Box>

            {/* Mobile */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={toggleMode} sx={{ color: 'text.primary' }} size="small">
                {dark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
              <LanguageSelector compact />
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'text.primary' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, px: 3, py: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Logo onClick={() => { goHome(); setDrawerOpen(false); }} />
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>

        <List>
          <ListItemButton onClick={() => { goHome(); setDrawerOpen(false); }} sx={{ borderRadius: 2 }}>
            <ListItemText primary={t('nav.home')} />
          </ListItemButton>
        </List>

        {CATEGORIES.map((cat) => (
          <Box key={cat.labelKey}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 1, mb: 0.5 }}>
              <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                {t(cat.labelKey)}
              </Box>
            </Box>
            <List dense>
              {cat.tools.map((tool) => (
                <ListItemButton key={tool.path} component={RouterLink} to={tool.path}
                  onClick={handleToolClick} sx={{ borderRadius: 2, gap: 1, color: 'text.primary' }}>
                  <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{tool.icon}</Box>
                  <ListItemText primary={t(tool.labelKey)} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}

        <Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ px: 1, mb: 0.5 }}>
            <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('nav.resources')}
            </Box>
          </Box>
          <List dense>
            {RESOURCES.map((item) => (
              <ListItemButton key={item.path} component={RouterLink} to={item.path}
                onClick={handleToolClick} sx={{ borderRadius: 2, gap: 1, color: 'text.primary' }}>
                <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                <ListItemText primary={t(item.labelKey)} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button fullWidth variant="contained" color="primary" onClick={scrollToTool}>
            {t('hero.cta')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
